import React, { useContext, useEffect, useRef, useState } from 'react';
import './Clubmodal.scss';
import TextField from '../TextField/TextField';
import Constant from '../../util/constant';
import HookForm from '../HookForm/HookForm';
import CustomButton from '../../components/CustomButton/button';
import CustomDropzone from '../../components/CustomDropzone/CustomDropzone';
import { Controller } from 'react-hook-form';
import MainContext from '../../MainContext';
import Spinner from 'react-bootstrap/Spinner'

// Every field here changes how apps already in users' hands behave, and none of
// it fails loudly: a mistyped bundle ID just stops push arriving, and a minimum
// version quietly starts nagging everyone below it on every single launch. So
// an edit to any of them is confirmed before it is saved.
const APP_CONFIG_FIELDS = [
  {
    name: 'bundleId',
    label: 'Bundle ID',
    consequence: 'Push notifications only reach devices whose app was built with this exact ID. The wrong value stops iOS push silently.'
  },
  {
    name: 'firebaseProjectId',
    label: 'Firebase project',
    consequence: 'Android push is sent through this project and only reaches devices whose app was built with its google-services.json. The wrong project stops Android push for the whole club.'
  },
  {
    name: 'appstoreUrl',
    label: 'App Store URL',
    consequence: 'This is where iOS update prompts send users. Without it neither iOS prompt appears at all.'
  },
  {
    name: 'playstoreUrl',
    label: 'Play Store URL',
    consequence: 'This is where Android update prompts send users. Without it neither Android prompt appears at all.'
  },
  {
    name: 'iosMinVersion',
    label: 'iOS minimum version',
    consequence: 'Every iOS user below this version gets the update prompt every time they open the app, until they update. They can dismiss it and carry on.'
  },
  {
    name: 'iosLatestVersion',
    label: 'iOS latest version',
    consequence: 'Changes which iOS users see the update prompt. Dismissing it silences it for a week.'
  },
  {
    name: 'androidMinVersion',
    label: 'Android minimum version',
    consequence: 'Every Android user below this version gets the update prompt every time they open the app, until they update. They can dismiss it and carry on.'
  },
  {
    name: 'androidLatestVersion',
    label: 'Android latest version',
    consequence: 'Changes which Android users see the update prompt. Dismissing it silences it for a week.'
  }
];

// Setting a previously blank field counts as a change too — going from blank to
// a minimum version is the single most disruptive edit on the form.
const appConfigChanges = (previous, next) =>
  APP_CONFIG_FIELDS.map((field) => {
    const before = ((previous && previous[field.name]) || '').trim();
    const after = ((next && next[field.name]) || '').trim();
    if (before === after) {
      return null;
    }
    let kind = 'Changed';
    if (!before) {
      kind = 'Set';
    }
    if (!after) {
      kind = 'Removed';
    }
    return { ...field, before, after, kind };
  }).filter(Boolean);

const Clubmodal = (props) => {
  let {
    closeClick,
    clubNamePlaceholder,
    leagueNamePlaceholder,
    leagueUrlPlaceholder,
    leagueModal,
    onSubmit,
    clubExistsError,
    defaultForm
  } = props;
  let addForm = {};
  console.log(props,'PROPS');
  // let defaultForm = {}
  const { isButtonLoading } = useContext(MainContext);
  // Holds the submitted values while the change summary is on screen. The form
  // itself stays mounted underneath so going back keeps everything typed.
  const [pendingChange, setPendingChange] = useState(null);
  // Adding a club has no previous values to compare against, so it submits
  // straight through.
  const isEdit = props._id !== '' && !leagueModal;

  // Changes are measured against what the modal opened with, not the live prop:
  // the clubs listing rewrites defaultForm down to just the club name while a
  // submit is in flight, so a second attempt after a rejected save would
  // otherwise read every populated field as newly set.
  const openedWith = useRef(defaultForm);

  useEffect(() => {
    if (props.showModal) {
      openedWith.current = defaultForm;
      return;
    }
    // The modal is unmounted between openings today; clearing on close keeps a
    // stale summary from reappearing if that ever stops being true.
    setPendingChange(null);
  }, [props.showModal]);

  const onFormSubmit = (values) => {
    const changes = isEdit ? appConfigChanges(openedWith.current, values) : [];
    if (changes.length === 0) {
      onSubmit(values);
      return;
    }
    setPendingChange({ values, changes });
  };

  const onConfirmChanges = () => {
    const { values } = pendingChange;
    // Drop back to the form before submitting so the existing button spinner and
    // the "club already exists" error still have somewhere to show.
    setPendingChange(null);
    onSubmit(values);
  };

  if (!leagueModal) {
    addForm = {
      sClubName: {
        name: 'sClubName',
        validate: {
          required: {
            value: true,
            message: "Club Name can't be empty"
          },
          pattern: {
            value: Constant.REGEX.OnlySpaces,
            message: "Club Name can't be empty"
          },
        },
      },
      sLogo: {
        name: 'sClubLogo',
        validate: {
          required: {
            value: true,
            message: "Please select club logo"
          },
        },
      },
      sLogoBgColour: {
        name: 'sLogoBgColour',
      },
      // All three are optional — a club is normally onboarded before its apps are
      // live — but a typo is worth catching here, since a bad bundleId only shows
      // up later as iOS push notifications silently failing to deliver.
      bundleId: {
        name: 'bundleId',
        validate: {
          validate: {
            isBundleId: (value) =>
              !value || Constant.REGEX.BundleId.test(value) || 'Must be reverse-DNS, e.g. com.cc.myclub',
          },
        },
      },
      // Blank means the default project. The API also rejects a project it holds no
      // credentials for; this only catches values that cannot be a project ID at all.
      firebaseProjectId: {
        name: 'firebaseProjectId',
        validate: {
          validate: {
            isFirebaseProjectId: (value) =>
              !value || Constant.REGEX.FirebaseProjectId.test(value) || 'Must be a Firebase project ID, e.g. communityclublive-2',
          },
        },
      },
      appstoreUrl: {
        name: 'appstoreUrl',
        validate: {
          validate: {
            isStoreUrl: (value) =>
              !value || Constant.REGEX.StoreUrl.test(value) || 'Must be a full http(s) address',
          },
        },
      },
      playstoreUrl: {
        name: 'playstoreUrl',
        validate: {
          validate: {
            isStoreUrl: (value) =>
              !value || Constant.REGEX.StoreUrl.test(value) || 'Must be a full http(s) address',
          },
        },
      },
      // Update prompts. Each platform is judged only against its own pair, because
      // the two version tracks drift apart whenever one store is slower to review.
      // Leaving a field blank switches that prompt off.
      iosMinVersion: {
        name: 'iosMinVersion',
        validate: {
          validate: {
            isVersion: (value) =>
              !value || Constant.REGEX.AppVersion.test(value) || 'Must be numbers and dots, e.g. 3.3',
          },
        },
      },
      iosLatestVersion: {
        name: 'iosLatestVersion',
        validate: {
          validate: {
            isVersion: (value) =>
              !value || Constant.REGEX.AppVersion.test(value) || 'Must be numbers and dots, e.g. 3.3',
          },
        },
      },
      androidMinVersion: {
        name: 'androidMinVersion',
        validate: {
          validate: {
            isVersion: (value) =>
              !value || Constant.REGEX.AppVersion.test(value) || 'Must be numbers and dots, e.g. 3.2',
          },
        },
      },
      androidLatestVersion: {
        name: 'androidLatestVersion',
        validate: {
          validate: {
            isVersion: (value) =>
              !value || Constant.REGEX.AppVersion.test(value) || 'Must be numbers and dots, e.g. 3.2',
          },
        },
      },
      oClubId: {
        name: 'oClubId',
        validate: {
          required: {
            value: true,
            message: "No club id"
          },
        },
      }
    }
  } else if (leagueModal) {
    // if (props._id !== '') {
    //   defaultForm = { 'sFixturesLogo': sFixturesLogo, 'sFixturesURL': sFixturesURL, 'sFixturesTitle': sFixturesTitle }
    // }
    addForm = {
      sFixturesTitle: {
        name: 'sFixturesTitle',
        validate: {
          required: {
            value: true,
            message: "League Title can't be empty"
          },
          pattern: {
            value: Constant.REGEX.OnlySpaces,
            message: "League Title can't be empty"
          },
        },
      },
      sLeagueUrl: {
        name: 'sFixturesURL',
        validate: {
          required: {
            value: true,
            message: "League URL can't be empty"
          },
          pattern: {
            value: Constant.REGEX.OnlySpaces,
            message: "League URL can't be empty"
          },
          validate: {
            hasValidUrl: (value) => (value && value.match(Constant.REGEX.Url)) || 'Invalid Url',
          },
        },
      },
      sLogo: {
        name: 'sFixturesLogo',
      }
    }
  }
  return (
    <div className="clubmodalMain">
      <HookForm
        defaultForm={defaultForm}
        onSubmit={(formMethod) => onFormSubmit(formMethod)}>
        {(formMethod) => {
          return (
            <div className={pendingChange ? "formSec isConfirming" : "formSec"}>
              <Controller
                name={addForm.sLogo.name}
                control={formMethod?.control}
                rules={addForm.sLogo.validate}
                errors={formMethod?.errors}
                as={<CustomDropzone {...props} type="clubDropzone" logoBgColour={!leagueModal ? formMethod?.watch('sLogoBgColour') : ''} />}
              />
              {!leagueModal ? <><TextField
                formMethod={formMethod}
                register={formMethod?.register(addForm.sClubName.validate)}
                name={addForm.sClubName.name}
                errors={formMethod?.errors}
                customError={{
                  name: addForm.sClubName.name,
                  message: clubExistsError,
                }}
                autoFocus={true}
                type="text"
                placeholder={clubNamePlaceholder}
              />
                <Controller
                  name={addForm.sLogoBgColour.name}
                  control={formMethod?.control}
                  defaultValue=""
                  render={({ onChange, value }) => (
                    <div className="logoBgColourSec">
                      <span className="logoBgColourLabel">Logo background colour</span>
                      <div className="logoBgColourControls">
                        <input
                          type="color"
                          value={value || '#ffffff'}
                          onChange={(e) => onChange(e.target.value)}
                        />
                        <span className="logoBgColourValue">{value || 'None'}</span>
                        {value && (
                          <a className="logoBgColourClear" onClick={() => onChange('')}>Clear</a>
                        )}
                      </div>
                    </div>
                  )}
                />
                <div className="appConfigSec">
                  <span className="appConfigHeading">App configuration</span>
                  <span className="appConfigHint">
                    Bundle ID is the APNs topic. Without it iOS push notifications are not delivered.
                    Firebase project is the one the club's Android app was registered in; leave it
                    blank for the default project.
                  </span>
                </div>
                <TextField
                  formMethod={formMethod}
                  register={formMethod?.register(addForm.bundleId.validate)}
                  name={addForm.bundleId.name}
                  errors={formMethod?.errors}
                  labelName="Bundle ID"
                  type="text"
                  placeholder="com.cc.myclub"
                />
                <TextField
                  formMethod={formMethod}
                  register={formMethod?.register(addForm.firebaseProjectId.validate)}
                  name={addForm.firebaseProjectId.name}
                  errors={formMethod?.errors}
                  labelName="Firebase project"
                  type="text"
                  placeholder="Blank for the default project"
                  hint="The project_id in the Android app's google-services.json, e.g. communityclublive-2. Leave blank if the app is in the default project."
                />
                <TextField
                  formMethod={formMethod}
                  register={formMethod?.register(addForm.appstoreUrl.validate)}
                  name={addForm.appstoreUrl.name}
                  errors={formMethod?.errors}
                  labelName="App Store URL"
                  type="text"
                  placeholder="https://apps.apple.com/..."
                />
                <TextField
                  formMethod={formMethod}
                  register={formMethod?.register(addForm.playstoreUrl.validate)}
                  name={addForm.playstoreUrl.name}
                  errors={formMethod?.errors}
                  labelName="Play Store URL"
                  type="text"
                  placeholder="https://play.google.com/store/apps/..."
                />
                <div className="appConfigSec">
                  <span className="appConfigHeading">App update prompts</span>
                  <span className="appConfigHint">
                    Neither setting blocks anyone. Both show the same dismissible
                    prompt, and differ only in how often it comes back. iOS and
                    Android are judged separately, so set each from that platform's
                    own released version number. Leave a field blank to switch that
                    prompt off. The matching store URL above is required for either
                    prompt to appear.
                  </span>
                </div>
                <TextField
                  formMethod={formMethod}
                  register={formMethod?.register(addForm.iosMinVersion.validate)}
                  name={addForm.iosMinVersion.name}
                  errors={formMethod?.errors}
                  labelName="iOS minimum version"
                  type="text"
                  placeholder="3.0"
                  hint="Persistent prompt. iPhone users on anything older are asked to update every time they open the app. They can dismiss it and carry on, nothing is blocked. Set it to the oldest iOS build you still support; raising it nags everyone below on every launch."
                />
                <TextField
                  formMethod={formMethod}
                  register={formMethod?.register(addForm.iosLatestVersion.validate)}
                  name={addForm.iosLatestVersion.name}
                  errors={formMethod?.errors}
                  labelName="iOS latest version"
                  type="text"
                  placeholder="3.3"
                  hint="Occasional prompt. Set this to your current released iOS build. Users below it are offered the update, and dismissing it keeps them quiet for a week or until you ship a newer version."
                />
                <TextField
                  formMethod={formMethod}
                  register={formMethod?.register(addForm.androidMinVersion.validate)}
                  name={addForm.androidMinVersion.name}
                  errors={formMethod?.errors}
                  labelName="Android minimum version"
                  type="text"
                  placeholder="3.0"
                  hint="Persistent prompt. Android users on anything older are asked to update every time they open the app. They can dismiss it and carry on, nothing is blocked. Set it to the oldest Android build you still support; raising it nags everyone below on every launch."
                />
                <TextField
                  formMethod={formMethod}
                  register={formMethod?.register(addForm.androidLatestVersion.validate)}
                  name={addForm.androidLatestVersion.name}
                  errors={formMethod?.errors}
                  labelName="Android latest version"
                  type="text"
                  placeholder="3.2"
                  hint="Occasional prompt. Set this to your current released Android build. Users below it are offered the update, and dismissing it keeps them quiet for a week or until you ship a newer version."
                />
              </>
                : <>
                  <TextField
                    formMethod={formMethod}
                    register={formMethod?.register(addForm.sFixturesTitle.validate)}
                    name={addForm.sFixturesTitle.name}
                    errors={formMethod?.errors}
                    autoFocus={true}
                    type="text"
                    placeholder={leagueNamePlaceholder}
                  />
                  <TextField
                    formMethod={formMethod}
                    register={formMethod?.register(addForm.sLeagueUrl.validate)}
                    name={addForm.sLeagueUrl.name}
                    errors={formMethod?.errors}
                    autoFocus={true}
                    type="text"
                    placeholder={leagueUrlPlaceholder}
                  />
                </>

              }

              <div className="btnGroup">
                <CustomButton type="submit"
                  title={!isButtonLoading ? props._id !== '' ? 'Update' : 'Add' : ''}
                  disabled={!formMethod?.formState.isValid || isButtonLoading}
                >
                  {isButtonLoading && <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />}
                </CustomButton>
                <CustomButton
                  className="cancelBtn" onClick={closeClick}
                  title="Cancel" />
              </div>
            </div>
          )
        }}
      </HookForm>
      {pendingChange && (
        <div className="confirmChangesSec">
          <span className="confirmChangesHeading">Confirm app configuration changes</span>
          <span className="confirmChangesHint">
            These settings affect apps already installed on users' phones. Check
            them before saving.
          </span>
          <ul className="confirmChangesList">
            {pendingChange.changes.map((change) => (
              <li key={change.name}>
                <div className="confirmChangeTop">
                  <span className="confirmChangeLabel">{change.label}</span>
                  <span className={"confirmChangeKind " + change.kind.toLowerCase()}>{change.kind}</span>
                </div>
                <div className="confirmChangeValues">
                  <span className={change.before ? "confirmChangeBefore" : "confirmChangeBefore isEmpty"}>
                    {change.before || 'Not set'}
                  </span>
                  <span className="confirmChangeArrow">→</span>
                  <span className="confirmChangeAfter">{change.after || 'Not set'}</span>
                </div>
                <span className="confirmChangeConsequence">{change.consequence}</span>
              </li>
            ))}
          </ul>
          <div className="btnGroup">
            <CustomButton type="button" title="Save changes" onClick={onConfirmChanges} />
            <CustomButton
              type="button"
              className="cancelBtn"
              title="Back"
              onClick={() => setPendingChange(null)} />
          </div>
        </div>
      )}
    </div>
  );
}
Clubmodal.defaultProps = {
  leagueModal: false,
  clubExistsError: ""
}

export default Clubmodal;
